import type { WizardFormData, TravelRhythm } from '../../../../types/wizard';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';

interface Step4RhythmProps {
  formData: WizardFormData;
  updateFormData: (data: Partial<WizardFormData>) => void;
  errors: Record<string, string>;
}

const Step4Rhythm = ({ formData, updateFormData, errors }: Step4RhythmProps) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const rhythms: Array<{
    value: TravelRhythm;
    label: string;
    description: string;
    icon: string;
    details: string;
  }> = [
    {
      value: 'relaxado',
      label: isEnglish ? 'Relaxed' : 'Relaxado',
      description: isEnglish ? 'Fewer activities per day, more free time' : 'Poucas atividades por dia, muito tempo livre',
      icon: 'ri-leaf-line',
      details: isEnglish ? '1-2 main activities per day' : '1-2 atividades principais por dia',
    },
    {
      value: 'equilibrado',
      label: isEnglish ? 'Balanced' : 'Equilibrado',
      description: isEnglish ? 'A balance between exploration and downtime' : 'Balanço entre exploração e descanso',
      icon: 'ri-scales-line',
      details: isEnglish ? '2-3 activities per day with breaks' : '2-3 atividades por dia com pausas',
    },
    {
      value: 'intenso',
      label: isEnglish ? 'Intense' : 'Intenso',
      description: isEnglish ? 'Make the most of every moment' : 'Aproveitar ao máximo cada momento',
      icon: 'ri-flashlight-line',
      details: isEnglish ? '4+ activities per day, faster pace' : '4+ atividades por dia, ritmo acelerado',
    },
  ];

  return (
    <div className="space-y-8">
      <StepCard>
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'What is the ideal pace for your trip?' : 'Qual o ritmo ideal para sua viagem?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'This defines how many activities we will include each day'
              : 'Isso define quantas atividades incluiremos em cada dia'}
          </p>
        </div>

        <div className="space-y-4">
          {rhythms.map((rhythm) => (
            <button
              key={rhythm.value}
              type="button"
              onClick={() => updateFormData({ step4_rhythm: rhythm.value })}
              className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                formData.step4_rhythm === rhythm.value
                  ? 'border-[#FFD700] bg-[#FFD700]/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    formData.step4_rhythm === rhythm.value
                      ? 'bg-[#C9A84C]/20 border border-[#C9A84C]/30 text-[#E8D4A0]'
                      : 'bg-white/10 text-white'
                  }`}
                >
                  <i className={`${rhythm.icon} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold text-lg mb-1">{rhythm.label}</p>
                  <p className="text-white/90 text-base mb-2">{rhythm.description}</p>
                  <p className="text-white/90 text-sm">{rhythm.details}</p>
                </div>
                {formData.step4_rhythm === rhythm.value && (
                  <i className="ri-checkbox-circle-fill text-[#E8D4A0] text-2xl flex-shrink-0"></i>
                )}
              </div>
            </button>
          ))}
        </div>

        {(errors.step4_rhythm || errors.rhythm) && (
          <p className="text-red-400 text-sm mt-4 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.step4_rhythm || errors.rhythm}
          </p>
        )}
      </StepCard>
    </div>
  );
};

export default Step4Rhythm;
